import { ChangeDetectionStrategy, Component, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DateTime } from 'luxon';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { Reserve } from '@/features/reserve/models/reserve';
import { DialogBase } from '@/types/dialog-base';
import { ReserveStatusBadgeComponent } from '@/features/reserve/components/reserve-status-badge.component';
import { JsonViewerComponent } from '@/shared/components/json-viewer.component';

export interface ReserveViewDialogData {
    reserve: Reserve;
}

@Component({
    selector: 'app-reserve-view-dialog',
    standalone: true,
    imports: [CommonModule, TableModule, ReserveStatusBadgeComponent, JsonViewerComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="grid gap-4">
            <section class="rounded-lg border border-surface-200 p-3">
                <h3 class="m-0 text-base font-semibold">Booking & Customer Support</h3>
                <div class="mt-3 grid gap-2 text-sm md:grid-cols-2">
                    <div><span class="text-color-secondary">Code:</span> {{ text(data.reserve.code) }}</div>
                    <div><span class="text-color-secondary">Invoice:</span> {{ text(data.reserve.gdInvoiceId) }}</div>
                    <div class="flex items-center gap-2"><span class="text-color-secondary">Status:</span> <app-reserve-status-badge [status]="data.reserve.status"></app-reserve-status-badge></div>
                    <div><span class="text-color-secondary">Course:</span> {{ text(data.reserve.courseName) }}</div>
                    <div><span class="text-color-secondary">Email:</span> {{ reserveEmail() }}</div>
                    <div><span class="text-color-secondary">Phone:</span> {{ text(data.reserve.user?.mobile || data.reserve.golferMobile) }}</div>
                    <div><span class="text-color-secondary">Tee Time:</span> {{ dateTime(data.reserve.golf?.slotTime) }}</div>
                    <div><span class="text-color-secondary">Players:</span> {{ num(data.reserve.golf?.persons) }}/{{ num(data.reserve.golf?.carts) }}/{{ num(data.reserve.golf?.caddies) }}</div>
                    <div><span class="text-color-secondary">Payment:</span> {{ text(data.reserve.payment) }}</div>
                    <div><span class="text-color-secondary">Gateway:</span> {{ text(data.reserve.paymentData?.gateway) }} / {{ text(data.reserve.paymentData?.sourceType) }}</div>
                    <div><span class="text-color-secondary">Created:</span> {{ dateTime(data.reserve.created) }}</div>
                    <div><span class="text-color-secondary">Paid:</span> {{ dateTime(data.reserve.paidAt) }}</div>
                    <div><span class="text-color-secondary">Expire At:</span> {{ dateTime(data.reserve.expireAt) }}</div>
                    <div><span class="text-color-secondary">Cancelled At:</span> {{ dateTime(data.reserve.revokeAt) }}</div>
                    <div class="md:col-span-2"><span class="text-color-secondary">Cancellation Cause:</span> {{ text(data.reserve.cancellationCause) }}</div>
                </div>
            </section>

            <section class="rounded-lg border border-surface-200 p-3">
                <h3 class="m-0 text-base font-semibold">Pricing Config</h3>
                <div class="mt-3 grid gap-2 text-sm md:grid-cols-2">
                    <div><span class="text-color-secondary">Price Category:</span> {{ text(data.reserve.golf?.priceCategory) }}</div>
                    <div><span class="text-color-secondary">Total Paid:</span> {{ money(data.reserve.price) }}</div>
                    <div><span class="text-color-secondary">GF Unit:</span> {{ money(data.reserve.golf?.slotPrice) }}</div>
                    <div><span class="text-color-secondary">Cart Unit:</span> {{ money(data.reserve.golf?.cartPrice) }}</div>
                    <div><span class="text-color-secondary">Caddy Unit:</span> {{ money(data.reserve.golf?.caddyPrice) }}</div>
                    <div><span class="text-color-secondary">Free Cart/Caddy:</span> {{ yesNo(data.reserve.freeCart) }} / {{ yesNo(data.reserve.freeCaddy) }}</div>
                    <div><span class="text-color-secondary">GF Total:</span> {{ money(data.reserve.golf?.slotsPrice) }}</div>
                    <div><span class="text-color-secondary">Cart Total:</span> {{ money(data.reserve.golf?.cartsPrice) }}</div>
                    <div><span class="text-color-secondary">Caddy Total:</span> {{ money(data.reserve.golf?.caddiesPrice) }}</div>
                </div>
                <div class="mt-3">
                    <div class="mb-2 text-sm font-semibold">Discount List</div>
                    <p-table [value]="data.reserve.discountList ?? []" class="p-datatable-sm">
                        <ng-template #header>
                            <tr>
                                <th>Type</th>
                                <th>Description</th>
                                <th class="text-right">Money</th>
                            </tr>
                        </ng-template>
                        <ng-template #body let-row>
                            <tr>
                                <td>{{ text(row.type) }}</td>
                                <td>{{ text(row.desc) }}</td>
                                <td class="text-right">{{ money(row.money) }}</td>
                            </tr>
                        </ng-template>
                        <ng-template #emptymessage>
                            <tr>
                                <td colspan="3" class="text-center text-color-secondary">No discounts</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </div>
            </section>

            <section class="rounded-lg border border-surface-200 p-3">
                <h3 class="m-0 text-base font-semibold">Settlement</h3>
                <div class="mt-3 grid gap-2 text-sm md:grid-cols-2">
                    <div><span class="text-color-secondary">Status:</span> {{ text(data.reserve.costs?.status) }}</div>
                    <div><span class="text-color-secondary">Green Fee Cost:</span> {{ money(data.reserve.costs?.greenfee) }}</div>
                    <div><span class="text-color-secondary">Cart Cost:</span> {{ money(data.reserve.costs?.cart) }}</div>
                    <div><span class="text-color-secondary">Caddy Cost:</span> {{ money(data.reserve.costs?.caddy) }}</div>
                    <div><span class="text-color-secondary">Adjusted Amount:</span> {{ money(data.reserve.costs?.adjustedAmount) }}</div>
                    <div><span class="text-color-secondary">Transfer Amount:</span> {{ money(data.reserve.costs?.transferAmount) }}</div>
                    <div><span class="text-color-secondary">Transfer Date:</span> {{ dateTime(data.reserve.costs?.transferDate) }}</div>
                    <div class="md:col-span-2"><span class="text-color-secondary">Note:</span> {{ text(data.reserve.costs?.note) }}</div>
                </div>
            </section>

            <section class="rounded-lg border border-surface-200 p-3">
                <div class="flex items-center justify-between">
                    <h3 class="m-0 text-base font-semibold">Developer Raw Data</h3>
                    <app-json-viewer [model]="data.reserve"></app-json-viewer>
                </div>
            </section>
        </div>
    `
})
export class ReserveViewDialogComponent extends DialogBase<ReserveViewDialogData> {
    constructor(@Optional() public override ref: DynamicDialogRef, @Optional() public override config: DynamicDialogConfig) {
        super(ref, config);
    }

    reserveEmail(): string {
        const reserveWithLegacy = this.data.reserve as Reserve & {
            useReserve?: { email?: string | null };
            userReserve?: { email?: string | null };
        };
        return this.text(reserveWithLegacy.useReserve?.email || reserveWithLegacy.userReserve?.email || this.data.reserve.user?.email);
    }

    text(value: unknown): string {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed || '-';
        }
        return value == null ? '-' : String(value);
    }

    money(value: unknown): string {
        if (typeof value !== 'number') {
            return '-';
        }
        return value.toLocaleString();
    }

    num(value: unknown): string {
        return typeof value === 'number' ? String(value) : '0';
    }

    yesNo(value: unknown): string {
        if (typeof value !== 'boolean') {
            return '-';
        }
        return value ? 'Yes' : 'No';
    }

    dateTime(value: string | Date | null | undefined): string {
        if (!value) {
            return '-';
        }
        const dt = typeof value === 'string' ? DateTime.fromISO(value) : DateTime.fromJSDate(value);
        return dt.isValid ? dt.toFormat('dd/MM/yyyy HH:mm') : '-';
    }
}
